import { useMemo } from 'react'

// Cache bust: 2025-01-15-12:00
export const groupsMetadata = {
  name: {
    showName: "Name",
    multiple: false,
    order: 0,
    editableProfile: true,
  },
  company: {
    showName: "Company",
    multiple: false,
    order: 1,
    editableProfile: true,
  },
  contact: {
    showName: "Contact",
    multiple: true,
    order: 2,
    editableProfile: true,
  },
  personal: {
    showName: "Personal",
    multiple: false,
    order: 3,
    editableProfile: true,
  },
  general: {
    showName: "General",
    multiple: true,
    order: 4,
    editableProfile: true,
  },
  payments: {
    showName: "Payments",
    multiple: false,
    order: 5,
    editableProfile: true,
  },
  videoConference: {
    showName: "Video Conference",
    multiple: false,
    order: 6,
    editableProfile: true,
  },
  social: {
    showName: "Social",
    multiple: false,
    order: 7,
    editableProfile: true,
  },
  followme: {
    showName: "Follow Me",
    multiple: false,
    order: 8,
    editableProfile: true,
  },
  additional: {
    showName: "Additional Fields",
    multiple: false,
    order: 9,
    editableProfile: true,
  },
  lists: {
    showName: "Lists",
    multiple: true,
    order: 10,
    editableProfile: true,
  }
}

// Property metadata with validation rules and field definitions
export const contactMetadata = {
  thumbnailImage: {
    types: ["Personal", "Professional", "Personal & Professional"],
    showName: "Photo",
    group: "name",
    vCard: "photo",
    multiple: true,
  },
  prefix: {
    types: ["Mr.", "Ms.", "Dr."],
    showName: "Name Prefix",
    group: "name",
    vCard: "namePrefix",
  },
  pronoun: {
    types: ["Him/He", "She/Her", "They", "Ze/Zir"],
    showName: "Personal Gender Pronouns",
    group: "additional",
    vCard: "x-pronoun",
  },
  sexualOrientation: {
    types: ["HeteroSexual", "HomoSexual", "BiSexual", "Gay", "Lesbian", "Asexual"],
    showName: "Sexual Orientation",
    group: "additional",
    vCard: "x-sexualOrientation",
  },
  givenName: {
    showName: "Given Name",
    group: "name",
    vCard: "givenName",
    verifiable: true,
  },
  familyName: {
    showName: "Family Name",
    group: "name",
    vCard: "familyName",
    verifiable: true,
  },
  middleName: {
    showName: "Middle Name",
    group: "name",
    vCard: "additionalName",
  },
  suffix: {
    showName: "Name Suffix",
    group: "name",
    vCard: "nameSuffix",
    types: ["Jr", "III", "Phd", "RCDD", "Attorney", "Esq", "MD", "DBA"],
  },
  nickName: {
    showName: "Nickname",
    group: "name",
    vCard: "nickname",
  },
  fn: {
    showName: "Full Name",
    group: "name",
    vCard: "fn",
  },
  phoneNumbers: {
    showName: "Phone Number",
    group: "contact",
    vCard: "tel",
    verifiable: true,
    multiple: true,
    fields: {
      1: { name: "number", showName: "Phone Number", vCard: "tel" },
      2: { name: "countryCode", showName: "Country Code", vCard: "countryCode", types: ["US (+1)", "CA (+1)", "UK (+44)", "DE (+49)", "FR (+33)", "IT (+39)", "ES (+34)", "AU (+61)", "JP (+81)", "CN (+86)", "IN (+91)", "BR (+55)", "MX (+52)", "RU (+7)", "ZA (+27)", "Other"] },
      3: { name: "type", showName: "Type", vCard: "type", types: ["mobile", "work", "home", "other"] }
    }
  },
  emailAddresses: {
    showName: "E-mail",
    group: "contact",
    vCard: "email",
    verifiable: true,
    multiple: true,
    fields: {
      1: { name: "address", showName: "E-mail Address", vCard: "email" },
      2: { name: "type", showName: "Type", vCard: "type", types: ["work", "personal", "other"] }
    }
  },
  postalAddresses: {
    showName: "Address",
    group: "contact",
    vCard: "adr",
    multiple: true,
    fields: {
      1: { name: "street", showName: "Street", vCard: "street" },
      2: { name: "city", showName: "City", vCard: "city" },
      3: { name: "state", showName: "State", vCard: "state" },
      4: { name: "postalCode", showName: "Postal Code", vCard: "postalCode" },
      5: { name: "country", showName: "Country", vCard: "country" },
      6: { name: "type", showName: "Type", vCard: "type", types: ["home", "work", "mailing", "billing", "shipping", "other"] }
    }
  },
  urlAddresses: {
    showName: "URL Address",
    group: "contact",
    vCard: "url",
    multiple: true,
    fields: {
      1: { name: "url", showName: "URL", vCard: "url" },
      2: { name: "type", showName: "Type", vCard: "type", types: ["website", "blog", "portfolio", "social", "business", "other"] }
    }
  },
  birthday: {
    showName: "Birthday",
    group: "personal",
    vCard: "bday",
    verifiable: true,
  },
  dates: {
    showName: "Date",
    group: "personal",
    vCard: "date",
    multiple: true,
    fields: {
      1: { name: "date", showName: "Date", vCard: "date" },
      2: { name: "type", showName: "Type", vCard: "type" }
    }
  },
  onmymind: {
    showName: "On my mind",
    group: "personal",
    vCard: "note",
  },
  howisaymyname: {
    showName: "How I say my name",
    group: "personal",
    vCard: "sound",
  },
  domain: {
    showName: "Domain",
    group: "company",
    vCard: "url",
  },
  general: {
    showName: "General",
    group: "general",
    vCard: "note",
    multiple: true,
  },
  personalNote: {
    showName: "Remember from",
    group: "general",
    vCard: "note",
  },
  venmo: {
    showName: "Venmo",
    group: "payments",
    vCard: "url",
  },
  paypal: {
    showName: "PayPal",
    group: "payments",
    vCard: "url",
  },
  cashapp: {
    showName: "Cash App",
    group: "payments",
    vCard: "url",
  },
  zelle: {
    showName: "Zelle",
    group: "payments",
    vCard: "url",
  },
  applepay: {
    showName: "Apple Pay",
    group: "payments",
    vCard: "url",
  },
  skype: {
    showName: "Skype",
    group: "videoConference",
    vCard: "url",
  },
  whereby: {
    showName: "Whereby (was Appear.in)",
    group: "videoConference",
    vCard: "url",
  },
  zoom: {
    showName: "Zoom",
    group: "videoConference",
    vCard: "url",
  },
  gotomeeting: {
    showName: "GoToMeeting",
    group: "videoConference",
    vCard: "url",
  },
  gotowebinar: {
    showName: "GoToWebinar",
    group: "videoConference",
    vCard: "url",
  },
  facebook: {
    showName: "Facebook",
    group: "social",
    vCard: "url",
  },
  linkedin: {
    showName: "LinkedIn",
    group: "social",
    vCard: "url",
  },
  twitter: {
    showName: "Twitter",
    group: "social",
    vCard: "url",
  },
  instagram: {
    showName: "Instagram",
    group: "social",
    vCard: "url",
  },
  whatsapp: {
    showName: "WhatsApp",
    group: "social",
    vCard: "url",
  },
  snapchat: {
    showName: "Snapchat",
    group: "social",
    vCard: "url",
  },
  tiktok: {
    showName: "TikTok",
    group: "followme",
    vCard: "url",
  },
  twitch: {
    showName: "Twitch",
    group: "followme",
    vCard: "url",
  },
  medium: {
    showName: "Medium",
    group: "followme",
    vCard: "url",
  },
  reddit: {
    showName: "Reddit",
    group: "followme",
    vCard: "url",
  },
  github: {
    showName: "Github",
    group: "followme",
    vCard: "url",
  },
  youtube: {
    showName: "YouTube",
    group: "followme",
    vCard: "url",
  },
  pinterest: {
    showName: "Pinterest",
    group: "followme",
    vCard: "url",
  },
  gender: {
    showName: "Gender",
    group: "additional",
    vCard: "gender",
    types: ["Male", "Female", "Agender", "Androgyne", "Bigender", "Cisgender", "Genderfluid", "Gender-nonconforming", "Genderqueer", "Intergender", "Intersex", "Omnigender", "Non-binary", "Questioning", "Transsexual", "Two-spirit"],
  },
  religion: {
    showName: "Religion",
    group: "additional",
    vCard: "religion",
    types: ["Agnostic", "Christian", "Catholic", "Jewish", "Protestant", "Muslim", "Hindu", "Atheist", "Buddist", "Islamic", "Pagan", "Sikh", "Taoist"],
  },
  political: {
    showName: "Political",
    group: "additional",
    vCard: "x-political",
    types: ["Apolitical", "Democrat", "Republican", "Libertarian", "Green", "Independent", "Democratic Socialist", "Green Party"],
  },
  astrological: {
    showName: "Astrological Sign",
    group: "additional",
    vCard: "astrological",
    types: ["Aquarius", "Pisces", "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn"],
  },
  chinesezodiac: {
    showName: "Chinese Zodiac",
    group: "additional",
    vCard: "x-chinesezodiac",
    types: ["Rat", "Ox", "Tiger", "Rabbit", "Dragon", "Snak", "Horse", "Goat", "Monkey", "Rooster", "Dog", "Pig"],
  },
  note: {
    showName: "Note",
    group: "general",
    vCard: "note",
  },
  jobTitle: {
    showName: "Job Title",
    group: "company",
    vCard: "title",
  },
  department: {
    showName: "Department",
    group: "company",
    vCard: "org",
  },
  company: {
    showName: "Company",
    group: "company",
    vCard: "organization",
  },
  dadjokes: {
    showName: "Dad Jokes",
    group: "lists",
    vCard: "note",
    multiple: true,
  },
  trivia: {
    showName: "Trivia",
    group: "lists",
    vCard: "note",
    multiple: true,
  },
  facts: {
    showName: "Facts",
    group: "lists",
    vCard: "note",
    multiple: true,
  },
  motivational: {
    showName: "Quotes",
    group: "lists",
    vCard: "note",
    multiple: true,
  },
  conversation: {
    showName: "Conversation",
    group: "lists",
    vCard: "note",
    multiple: true,
  },
  educational: {
    showName: "Educational",
    group: "lists",
    vCard: "note",
    multiple: true,
  },
  jokes: {
    showName: "Jokes",
    group: "lists",
    vCard: "note",
    multiple: true,
  }
}

// Property order within each group (matching profile screen order)
export const propertyOrder = {
  name: ['thumbnailImage', 'prefix', 'givenName', 'nickName', 'middleName', 'familyName', 'suffix'],
  company: ['company', 'jobTitle', 'department', 'domain'],
  contact: ['phoneNumbers', 'emailAddresses', 'postalAddresses', 'urlAddresses'],
  personal: ['onmymind', 'howisaymyname', 'birthday', 'dates'],
  general: ['general', 'note', 'personalNote'],
  payments: ['venmo', 'paypal', 'cashapp', 'zelle', 'applepay'],
  videoConference: ['skype', 'whereby', 'zoom', 'gotomeeting', 'gotowebinar'],
  social: ['facebook', 'linkedin', 'twitter', 'instagram', 'whatsapp', 'snapchat'],
  followme: ['tiktok', 'twitch', 'medium', 'reddit', 'github', 'youtube', 'pinterest'],
  additional: ['gender', 'pronoun', 'sexualOrientation', 'religion', 'political', 'astrological', 'chinesezodiac']
}

// Hook to process profile properties into grouped metadata
export const useContactMetadata = (properties) => {
  return useMemo(() => {
    if (!properties || !Array.isArray(properties)) {
      return []
    }

    // Get user ID from existing properties to generate placeholder objectIds
    const idProp = properties.find(p => p && p.objectId && p.objectId.includes('_'))
    const userId = (idProp && idProp.objectId) ? idProp.objectId.split('_').pop() : 'default'

    // Group all available properties by their group
    const grouped = {}

    // Initialize all groups and their properties (with empty placeholders)
    Object.keys(groupsMetadata).forEach(groupName => {
      const groupMeta = groupsMetadata[groupName]
      grouped[groupName] = {
        ...groupMeta,
        name: groupName,
        properties: {}
      }

      // Initialize placeholders for all properties in this group
      const order = propertyOrder[groupName] || []
      order.forEach(propertyName => {
        const metadata = contactMetadata[propertyName]
        if (!metadata) return

        grouped[groupName].properties[propertyName] = {
          name: propertyName,
          value: '',
          objectId: `${propertyName}_${userId}`,
          verified: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          ...metadata,
          showName: metadata.showName
        }
      })
    })

    // Filter out "fn" (Full Name) property - it's computed from givenName and familyName
    const filteredProperties = properties.filter(property => property.name !== 'fn')

    // Fill in real properties from the profile
    filteredProperties.forEach(property => {
      const metadata = contactMetadata[property.name]
      if (!metadata) {
        return
      }

      const groupName = metadata.group
      if (!grouped[groupName]) {
        grouped[groupName] = {
          name: groupName,
          properties: {}
        }
      }

      // Extract value from parameters or direct value
      const processedProperty = {
        ...property,
        ...metadata,
        showName: metadata.showName,
        value: property.multiple && property.values && property.values.length > 0
          ? (property.values[0].parameters ? property.values[0].parameters['1']?.value : property.values[0].value)
          : (property.parameters && property.parameters["1"]?.value) || property.value
      }
      
      // Preserve values array if it exists at root level (server format for multiple properties)
      if (property.multiple && property.values && Array.isArray(property.values) && property.values.length > 0) {
        processedProperty.values = property.values
      }

      // Handle placeholder replacement vs merging multiple properties
      const isPlaceholder = grouped[groupName].properties[property.name]?.objectId === `${property.name}_${userId}`

      if (!isPlaceholder && grouped[groupName].properties[property.name]) {
        const existingProperty = grouped[groupName].properties[property.name]
        // If both have values arrays, merge them (avoid duplicates by objectId)
        if (existingProperty.values && Array.isArray(existingProperty.values) && 
            processedProperty.values && Array.isArray(processedProperty.values)) {
          const existingIds = new Set(existingProperty.values.map(v => v.objectId))
          const newValues = processedProperty.values.filter(v => !existingIds.has(v.objectId))
          existingProperty.values = [...existingProperty.values, ...newValues]
          
          if (existingProperty.values.length > 0 && property.name === 'postalAddresses') {
            const firstParams = existingProperty.values[0].parameters || {};
            const getParamValue = (key) => firstParams[String(key)]?.value || '';
            const street = getParamValue('1');
            const city = getParamValue('2');
            const state = getParamValue('3');
            const postalCode = getParamValue('4');
            const country = getParamValue('5');
            const addressParts = [street, city, state, postalCode, country].filter(Boolean);
            existingProperty.value = addressParts.length > 0 ? addressParts.join(', ') : 'Address';
          } else {
            existingProperty.value = processedProperty.value;
          }
        } else if (processedProperty.values && Array.isArray(processedProperty.values)) {
          existingProperty.values = processedProperty.values
          existingProperty.value = processedProperty.value
        } else if (existingProperty.values && Array.isArray(existingProperty.values)) {
          const existingIds = new Set(existingProperty.values.map(v => v.objectId))
          if (!existingIds.has(processedProperty.objectId)) {
            existingProperty.values.push({
              objectId: processedProperty.objectId,
              verified: processedProperty.verified,
              parameters: processedProperty.parameters,
              createdAt: processedProperty.createdAt,
              updatedAt: processedProperty.updatedAt
            })
          }
        } else {
          const values = []
          if (existingProperty.objectId && existingProperty.objectId !== `${property.name}_${userId}`) {
            values.push({
              objectId: existingProperty.objectId,
              verified: existingProperty.verified,
              parameters: existingProperty.parameters,
              createdAt: existingProperty.createdAt,
              updatedAt: existingProperty.updatedAt
            })
          }
          if (processedProperty.objectId && processedProperty.objectId !== existingProperty.objectId) {
            values.push({
              objectId: processedProperty.objectId,
              verified: processedProperty.verified,
              parameters: processedProperty.parameters,
              createdAt: processedProperty.createdAt,
              updatedAt: processedProperty.updatedAt
            })
          }
          if (values.length > 0) {
            existingProperty.values = values
            existingProperty.multiple = true
          }
        }
        return
      }

      // Special handling for phoneNumbers - extract the number from parameters
      if (property.name === 'phoneNumbers' && property.parameters && property.parameters["1"]) {
        if (metadata.multiple && !property.values) {
          processedProperty.values = [{
            objectId: property.objectId,
            verified: property.verified,
            parameters: property.parameters,
            createdAt: property.createdAt,
            updatedAt: property.updatedAt
          }]
        }
      }

      // Special handling for emailAddresses - extract the email from parameters
      if (property.name === 'emailAddresses' && property.parameters && property.parameters["1"]) {
        processedProperty.value = property.parameters["1"].value
      }

      // Special handling for onmymind - extract the text value
      if (property.name === 'onmymind') {
        if (property.parameters && property.parameters["1"] && property.parameters["1"].value) {
          processedProperty.value = property.parameters["1"].value
        } else if (property.value && typeof property.value === 'string') {
          processedProperty.value = property.value
        } else if (property.value && typeof property.value === 'object' && property.value.value) {
          processedProperty.value = property.value.value
        }
      }

      // Special handling for postalAddresses - extract address string from parameters
      if (property.name === 'postalAddresses') {
        const getParamValue = (params, key) => {
          if (!params || typeof params !== 'object') return '';
          const strKey = String(key);
          return params[strKey]?.value || '';
        };

        if (property.values && Array.isArray(property.values) && property.values.length > 0) {
          const params = property.values[0].parameters || {};
          const street = getParamValue(params, '1');
          const city = getParamValue(params, '2');
          const state = getParamValue(params, '3');
          const postalCode = getParamValue(params, '4');
          const country = getParamValue(params, '5');
          const addressParts = [street, city, state, postalCode, country].filter(Boolean);
          processedProperty.value = addressParts.length > 0 ? addressParts.join(', ') : 'Address';
        } else if (property.parameters) {
          const params = property.parameters;
          const street = getParamValue(params, '1');
          const city = getParamValue(params, '2');
          const state = getParamValue(params, '3');
          const postalCode = getParamValue(params, '4');
          const country = getParamValue(params, '5');
          const addressParts = [street, city, state, postalCode, country].filter(Boolean);
          processedProperty.value = addressParts.length > 0 ? addressParts.join(', ') : 'Address';
        }
      }

      // Special handling for address properties with parameters at root level (legacy format)
      if ((property.name === 'emailAddresses' || property.name === 'urlAddresses') &&
        property.parameters) {
        processedProperty.value = property.parameters
      }

      grouped[groupName].properties[property.name] = processedProperty
    })

    // Sort properties within each group by propertyOrder
    Object.keys(grouped).forEach(groupName => {
      const order = propertyOrder[groupName] || []
      if (order.length > 0 && grouped[groupName].properties) {
        const propsArray = Object.entries(grouped[groupName].properties)
        propsArray.sort((a, b) => {
          const indexA = order.indexOf(a[0])
          const indexB = order.indexOf(b[0])
          if (indexA === -1 && indexB === -1) return 0
          if (indexA === -1) return 1
          if (indexB === -1) return -1
          return indexA - indexB
        })
        grouped[groupName].properties = Object.fromEntries(propsArray)
      }
    })

    // Convert to array and sort by order
    const result = Object.values(grouped)
      .map(group => ({
        ...group,
        description: getGroupDescription(group.showName)
      }))
      .sort((a, b) => a.order - b.order)

    return result
  }, [properties])
}

// Get description for each group
const getGroupDescription = (groupName) => {
  const descriptions = {
    "Name": "Your personal identification information",
    "Company": "Company and business information",
    "Contact": "Contact and communication information",
    "Personal": "Personal notes, thoughts and dates",
    "General": "General notes and information",
    "Payments": "Payment and financial services",
    "Video Conference": "Video conferencing platforms",
    "Social": "Social media profiles",
    "Follow Me": "Social platforms to follow",
    "Additional Fields": "Additional personal information",
    "Lists": "Personal lists of jokes, trivia, facts and quotes"
  }
  return descriptions[groupName] || ""
}